import * as React from 'react';
import { CLIENT_TITLE } from "./constants";
import { Button } from "@mantine/core";
import { IconSearch, IconTableExport } from "@tabler/icons-react";
import axios from "axios";
import { ISongStat } from "./types";
import { Column, useTable } from "react-table";
import { DateRangePicker, DateRangePickerValue } from "@mantine/dates";
import dayjs from "dayjs";
import { useTableStyles } from './useTableStyles';
import styles from "../../components/table/table.module.scss";
import { utils, writeFile } from 'xlsx';
import Page from "../../components/layout/Page";
import { useTableSort } from "../../hooks";
import { getSortedList } from "../../utils/sort";

interface ISongStatusFilter {
    startDate: string;
    endDate: string;
}

interface ISongStatResponse {
    status: string;
    filter: ISongStatusFilter;
    result: ISongStat[];
}

const SongStatsList = () => {

    const { classes } = useTableStyles();

    const [ records, setRecords ] = React.useState<ISongStat[]>([]);
    const [ dateRange, setDateRange ] = React.useState<DateRangePickerValue>([ null, null ]);
    const { sort, setSort, getSort } = useTableSort();

    React.useEffect(() => {
        void fetchData();
    }, []);

    const data = React.useMemo(() => {
        return getSortedList(sort, records);
    }, [records, sort]);

    const columns: Column<ISongStat>[] = React.useMemo(
        () => [
            {
                Header: 'Код',
                accessor: 'code',
            },
            {
                Header: 'Дуучин',
                accessor: 'singer',
            },
            {
                Header: 'Дуу',
                accessor: 'song',
            },
            {
                Header: 'Үг',
                accessor: 'lyricBy',
            },
            {
                Header: 'Ая',
                accessor: 'musicBy',
                Footer: 'Нийт:'
            },
            {
                Header: 'Тоглуулсан тоо',
                accessor: 'playedCount',
                Footer: (info) => {
                    const total = React.useMemo(
                        () =>
                            info.rows.reduce((sum, row) => row.values.playedCount + sum, 0),
                        [ info.rows ]
                    );
                    return <div>
                        {total}
                    </div>
                }
            },
            {
                Header: 'Тоглуулсан хэрэглэгч',
                accessor: 'userCount',
                Footer: (info) => {
                    const total = React.useMemo(
                        () =>
                            info.rows.reduce((sum, row) => row.values.userCount + sum, 0),
                        [ info.rows ]
                    );
                    return <div>
                        {total}
                    </div>
                }
            },
            {
                Header: 'Зөвшөөрөл үг',
                accessor: 'licenseLyric',
            },
            {
                Header: 'Зөвшөөрөл ая',
                accessor: 'licenseSound',
            },
            {
                Header: 'Зөвшөөрөл бичлэг',
                accessor: 'licenseVideo',
            },
            {
                Header: 'Зөвшөөрөл дуучин',
                accessor: 'licenseSinger',
            },
        ],
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        footerGroups
    } = useTable({
        columns,
        data,
        defaultColumn: {
            defaultCanSort: true
        }
    });

    const fetchData = async (params?: Record<string, any>) => {
        const { data } = await axios.post<ISongStatResponse>(`/api/play-history/song-stats`, params);
        setRecords(data.result);
        const startDate = dayjs(data.filter.startDate).toDate();
        const endDate = dayjs(data.filter.endDate).toDate();
        setDateRange([startDate, endDate]);
    }

    const handleExcelExport = () => {
        const header = (columns.map((column) => column.accessor)) as string[];
        const worksheet = utils.json_to_sheet(data, {
            header
        });
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "Дууны тоолуур");

        utils.sheet_add_aoa(worksheet, [columns.map((column) => column.Header)], { origin: "A1" });

        const [ start, end ] = dateRange.map((date) => dayjs(date).format('YYYY.MM.DD'));
        const fileName = `${start}-${end}`;

        writeFile(workbook, `song-stats-${fileName}.xlsx`);
    }
    
    const handleSearchClick = () => {
        const [ startDate, endDate ] = dateRange.map((date) => dayjs(date).format('YYYY.MM.DD'));

        const params = {
            startDate,
            endDate
        }
        void fetchData(params);
    }

    const handleSort = (id: string) => {
        const sortObj = getSort(id);
        setSort(sortObj);
    }

    return (
        <Page>
            <Page.Header title={CLIENT_TITLE}/>
            <Page.Body>
                <div style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ width: 350, display: 'flex', gap: '1rem' }}>
                            <DateRangePicker
                                placeholder="Өдөр сонгоно уу"
                                value={dateRange}
                                onChange={setDateRange}
                                style={{ width: '100%' }}
                                clearable={false}
                            />
                            <Button
                                leftIcon={<IconSearch/>}
                                onClick={handleSearchClick}
                            >
                                Хайх...
                            </Button>
                        </div>
                        <Button onClick={handleExcelExport} rightIcon={<IconTableExport/>}>
                            Excel Export
                        </Button>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', marginTop: '1rem' }}>
                        <table {...getTableProps({ className: styles.table })}>
                            <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps({ className: classes.headerRow })}>
                                    {headerGroup.headers.map(column => {
                                        let sortText = '';
                                        if (sort?.[column.id] === 1) {
                                            sortText = '🔼'
                                        } else if (sort?.[column.id] === -1) {
                                            sortText = '🔽'
                                        }
                                        return (
                                            <th
                                                {...column.getHeaderProps({ className: styles.headerCell })}
                                                onClick={() => column.defaultCanSort && handleSort(column.id)}
                                            >
                                                {column.render('Header')}
                                                {sortText}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps({ className: styles.row })}>
                                        {row.cells.map(cell => {
                                            return (
                                                <td
                                                    {...cell.getCellProps({ className:  styles.cell })}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                            </tbody>
                            <tfoot>
                            {footerGroups.map(group => (
                                <tr {...group.getFooterGroupProps()}>
                                    {group.headers.map(column => (
                                        <td {...column.getFooterProps({ className: styles.cell, style: { fontWeight: 700 } })}>{column.render('Footer')}</td>
                                    ))}
                                </tr>
                            ))}
                            </tfoot>
                        </table>
                    </div>
                </div>
            </Page.Body>
        </Page>
    )
};

export default SongStatsList;